import type {
    ActorEvent,
    FieldADTValue,
    HighLightConditions,
    RecordLikeProtocol,
    RichTextContentProtocol,
    ViewField
} from '@lighthouse/core'
import { useTableSelection, VIEW_CELL_DATA_KEY, VIEW_FIELD_DATA_KEY } from '@lighthouse/shared'
import { useVirtualizer } from '@tanstack/react-virtual'
import chroma from 'chroma-js'
import React, { useEffect, useMemo, useTransition } from 'react'
import styled from 'styled-components'

import { useViewBlockContext } from '../../context'
import type { AdvancedTableContentState, AdvancedTableTooltipState } from './AdvancedTableContent'
import AdvancedTableRecord from './AdvancedTableRecord'

interface AdvancedTableBodyProps {
    checkable?: boolean
    selectedRecords?: string[]
    viewFields: ViewField[]
    scrollRef: React.RefObject<HTMLDivElement>
    highLightRules?: HighLightConditions
    blockWidth: number
    primaryDataSourceFieldIds?: Set<string>
    onRecordSelect?: (recordIds: string[]) => void
    onOpenEditor?: (data: AdvancedTableContentState) => void
    onOpenTooltip?: (data: AdvancedTableTooltipState) => void
    onCellChange?: (recordId: string, fieldValue: FieldADTValue) => Promise<boolean>
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRenderButtonTitle: (v: RichTextContentProtocol, record?: RecordLikeProtocol) => string
}

const SCxAdvancedTableContainer = styled.div``

const AdvancedTableBody: React.FC<AdvancedTableBodyProps> = ({
    checkable,
    selectedRecords,
    viewFields,
    scrollRef,
    highLightRules,
    blockWidth,
    primaryDataSourceFieldIds,
    onRecordSelect,
    onOpenEditor,
    onOpenTooltip,
    onCellChange,
    onRecordOperatorActionTrigger,
    onRecordClick,
    onRenderButtonTitle
}) => {
    const { recordPoints } = useViewBlockContext()
    useTableSelection({
        scrollRef,
        cellDataKey: VIEW_CELL_DATA_KEY,
        fieldDataKey: VIEW_FIELD_DATA_KEY
    })
    const [_, startTransition] = useTransition()

    const documentStyles = getComputedStyle(document.body)
    const themeColor = documentStyles.getPropertyValue('--color-main').trim()
    const hoverCellColor = chroma(themeColor).alpha(0.3).hex()

    const overscan = useMemo(() => {
        let width = blockWidth
        return viewFields.reduce<number>((prev, cur) => {
            if (width > 0) {
                width -= cur.width || 160
                return ++prev
            }
            return prev
        }, 0)
    }, [blockWidth, viewFields])

    const columnVirtualizer = useVirtualizer({
        horizontal: true,
        count: viewFields.length,
        getScrollElement: () => scrollRef.current,
        paddingStart: 60,
        paddingEnd: 36,
        estimateSize: index => viewFields[index].width || 160,
        overscan
    })

    useEffect(() => {
        startTransition(() => {
            columnVirtualizer.measure()
        })
    }, [columnVirtualizer, viewFields, blockWidth])

    return (
        <SCxAdvancedTableContainer
            style={{
                width: '100%',
                position: 'relative'
            }}
        >
            {recordPoints.map((point, index) => {
                return (
                    <AdvancedTableRecord
                        key={point.id}
                        id={point.id}
                        index={index}
                        checkable={checkable}
                        selectedRecords={selectedRecords}
                        viewFields={viewFields}
                        highLightRules={highLightRules}
                        columnVirtualItems={columnVirtualizer.getVirtualItems()}
                        hoverCellColor={hoverCellColor}
                        primaryDataSourceFieldIds={primaryDataSourceFieldIds}
                        onRecordSelect={onRecordSelect}
                        onOpenEditor={onOpenEditor}
                        onOpenTooltip={onOpenTooltip}
                        onCellChange={onCellChange}
                        onRecordOperatorActionTrigger={onRecordOperatorActionTrigger}
                        onRecordClick={onRecordClick}
                        onRenderButtonTitle={onRenderButtonTitle}
                    />
                )
            })}
        </SCxAdvancedTableContainer>
    )
}

export default AdvancedTableBody
